import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../Components/Seo/Seo";
import { getCategories } from "../api/storefront";
import { getImageUrl, joinPublicStorefrontUrl } from "../api/client";
import { usePrerenderReady } from "../utils/prerenderReady";
import {
  buildCategoryLandingContent,
  categoryDisplayName,
  categoryLandingPath,
  isIndexableCategory,
  isPublishedCategory,
  productImage,
  productPriceLabel,
} from "./CategoryLandingContent";
import "./CategoryLanding.css";

function fetchJson(path) {
  return fetch(joinPublicStorefrontUrl(path), { cache: "no-store" }).then((res) => {
    if (!res.ok) return null;
    return res.json();
  });
}

function RichText({ className = "", html }) {
  return <div className={`categoryLandingRichText ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html || "" }} />;
}

export default function CategoryLanding() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [relatedCategories, setRelatedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProductsLoading(true);
    setNotFound(false);
    setCategory(null);
    setProducts([]);

    fetchJson(`/api/categories/${encodeURIComponent(slug || "")}`)
      .then(async (data) => {
        if (cancelled) return;
        if (!isPublishedCategory(data)) {
          setNotFound(true);
          setProductsLoading(false);
          return;
        }
        setCategory(data);
        const qs = new URLSearchParams({ categoryId: String(data.id) });
        const productData = await fetchJson(`/api/products?${qs.toString()}`);
        if (cancelled) return;
        setProducts(Array.isArray(productData) ? productData : []);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setProductsLoading(false);
        }
      });

    getCategories().then((list) => {
      if (!cancelled) setRelatedCategories(Array.isArray(list) ? list : []);
    });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  usePrerenderReady(!loading && !productsLoading && (Boolean(category) || notFound));

  const content = useMemo(() => buildCategoryLandingContent(category, products), [category, products]);
  const name = categoryDisplayName(category);
  const canonicalPath = `/category/${encodeURIComponent(slug || "")}`;
  const visibleRelated = relatedCategories
    .filter((item) => item?.id !== category?.id && isPublishedCategory(item))
    .slice(0, 4);

  if (loading || productsLoading) {
    return (
      <main className="categoryLanding categoryLandingLoading" aria-live="polite">
        <Seo title="Category rentals | Gem Fashion Studios" description="Loading rental category." canonicalPath={canonicalPath} noindex />
        <p>Loading curated rentals…</p>
      </main>
    );
  }

  if (notFound || !category) {
    return (
      <main className="categoryLanding categoryLandingNotFound">
        <Seo
          title="Category not found | Gem Fashion Studios"
          description="This rental category is not available. Browse the latest outfits on rent at Gem Fashion Studios."
          canonicalPath={canonicalPath}
          noindex
        />
        <section className="categoryLandingPanel">
          <p className="categoryLandingEyebrow">Category unavailable</p>
          <h1>Rental category not found</h1>
          <p>This category may be unpublished or no longer available.</p>
          <Link className="categoryLandingCta" to="/shop">
            Browse all rentals
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="categoryLanding">
      <Seo title={content.title} description={content.description} canonicalPath={canonicalPath} noindex={!isIndexableCategory(category)} />

      <section className="categoryLandingHero">
        <div>
          <p className="categoryLandingEyebrow">Premium outfit rentals</p>
          <h1>{name} on Rent</h1>
          <RichText className="categoryLandingIntro" html={content.intro} />
          <div className="categoryLandingActions">
            <a className="categoryLandingCta" href="#category-products">
              Browse {name}
            </a>
            <Link className="categoryLandingSecondary" to={`/shop?categoryId=${category.id}`}>
              Use shop filters
            </Link>
          </div>
        </div>
        <div className="categoryLandingHeroCard" aria-label={`${products.length} ${name} rental options`}>
          <strong>{products.length || "Curated"}</strong>
          <span>{products.length === 1 ? "look available" : "looks available"}</span>
        </div>
      </section>

      <section className="categoryLandingSection">
        <h2>Style notes for {name}</h2>
        <div className="categoryLandingCards">
          {content.sections.map((item) => (
            <article key={`${item.title}-${item.body}`}>
              <h3>{item.title}</h3>
              <RichText html={item.body} />
            </article>
          ))}
        </div>
      </section>

      <section className="categoryLandingSection">
        <h2>Occasion guidance for {name}</h2>
        <div className="categoryLandingCards">
          {content.occasions.map((item) => (
            <article key={`${item.title}-${item.body}`}>
              <h3>{item.title}</h3>
              <RichText html={item.body} />
            </article>
          ))}
        </div>
      </section>

      <section className="categoryLandingSection categoryLandingProcess">
        <h2>How the rental process works</h2>
        <div className="categoryLandingSteps">
          {content.rentalProcess.map((item) => (
            <article key={`${item.title}-${item.body}`}>
              <h3>{item.title}</h3>
              <RichText html={item.body} />
            </article>
          ))}
        </div>
        <div className="categoryLandingBenefitGrid">
          {content.rentalBenefits.map((item) => (
            <article key={`${item.title}-${item.body}`}>
              <h3>{item.title}</h3>
              <RichText html={item.body} />
            </article>
          ))}
        </div>
      </section>

      <section id="category-products" className="categoryLandingSection">
        <h2>Browse {name} rental products</h2>
        {products.length ? (
          <div className="categoryLandingGrid">
            {products.map((product) => {
              const img = getImageUrl(productImage(product));
              return (
                <article className="categoryProductCard" key={product.id}>
                  <Link to={`/product/${product.id}`} className="categoryProductImage">
                    {img ? <img src={img} alt={product.name || `${name} rental`} loading="lazy" decoding="async" /> : null}
                    <span>View rental</span>
                  </Link>
                  <div>
                    <h3>
                      <Link to={`/product/${product.id}`}>{product.name || `${name} rental look`}</Link>
                    </h3>
                    <p>{productPriceLabel(product)}</p>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p>No products are currently listed in this category. Check the full shop for fresh arrivals.</p>
        )}
      </section>

      <section className="categoryLandingSection categoryLandingRelated" aria-labelledby="related-category-heading">
        <h2 id="related-category-heading">Related rental categories</h2>
        <div className="categoryLandingRelatedLinks">
          {content.relatedCategoryLinks.length
            ? content.relatedCategoryLinks.map((item) => (
                <Link key={item.url} to={item.url}>
                  {item.label}
                </Link>
              ))
            : visibleRelated.map((item) => (
                <Link key={item.id || item.name} to={categoryLandingPath(item)}>
                  {categoryDisplayName(item)}
                </Link>
              ))}
          {content.relatedProductLinks.map((item) => (
            <Link key={item.url} to={item.url}>
              {item.label}
            </Link>
          ))}
          <Link to="/shop">All rental outfits</Link>
        </div>
      </section>

      <section className="categoryLandingSection categoryLandingFaq" aria-labelledby="category-faq-heading">
        <h2 id="category-faq-heading">Frequently asked questions</h2>
        {content.faqs.map((faq) => (
          <article key={faq.q} className="categoryLandingFaqItem">
            <h3>{faq.q}</h3>
            <RichText html={faq.a} />
          </article>
        ))}
      </section>

      <section className="categoryLandingFinalCta">
        <h2>Ready to rent {name}?</h2>
        <p>{content.cta.body}</p>
        <Link className="categoryLandingCta" to={content.cta.url}>
          {content.cta.label}
        </Link>
      </section>
    </main>
  );
}
